import { Alert, AlertTitle, Button, Snackbar } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchLatestBidEventByReferenceNumber } from "../../api/bid";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import WarningDialog from "../../components/fundamentals/WarningDialog";
import PageLoading from "../../components/PageLoading";
import UnauthorizedMessage from "../../components/UnauthorizedMessage";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { removeBidAdminEvent, selectWebSocketEventData } from "../../slices/webSocketEvent/webSocketEventSlice.js";
import { logout } from "../../util/authentication";
import { BID_TYPE } from "../../util/bid/bidConstants";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber";
import { getMultiStationAdminStationList } from "../../util/commonUtils.js";
import { NO_ACCESS_TO_PAGE_MESSAGE } from "../../util/messageConstants";
import ManageFreedayBidPage from "./ManageFreedayBidPage";
import ManageIAMFTOBidPage from "./ManageIAMFTOBidPage";
import ManageShiftBidPage from "./ManageShiftBidPage";
import ManageTWUDATFTOBidPage from "./ManageTWUDATFTOBidPage";
import ManageIAMDATBidPage from "./ManageIAMDATBidPage";
import ManageNCPTOFTOBidPage from "./ManageNCPTOFTOBidPage";
import ManageNCPTOGOCHOLBidPage from "./ManageNCPTOGOCHOLBidPage";

const ManageBidPage = (props) => {
  const location = useLocation();
  const userSession = useSelector(selectUserSession);
  const dispatch = useDispatch();
  const webSocketEventData = useSelector(selectWebSocketEventData);

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [hasAccessToViewPage, setHasAccessToViewPage] = useState(false);
  const [selectedTab, setSelectedTab] = useState(location.state?.isAwardClicked ? 3 : 0);
  const [bidData, setBidData] = useState({});
  const [loadErrors, setLoadErrors] = useState(false);
  const [isNewEventAvailable, setIsNewEventAvailable] = useState(false);
  const currentRequestId = useRef(null);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      loadBidMetaData();
    }
    // eslint-disable-next-line
  }, [isUserAuthorizedState]);

  useEffect(() => {
    if (Object.values(webSocketEventData.bidAdminEvents).length > 0) {
      const wsEventForCurrentBid = webSocketEventData.bidAdminEvents[bidData.referenceNumber];
      const isVersionUpToDate = parseInt(bidData.v) >= parseInt(wsEventForCurrentBid?.v);
      const isFromCurrentSave = currentRequestId.current === wsEventForCurrentBid?.requestId;
      if (wsEventForCurrentBid && !isVersionUpToDate) {
        if (!isFromCurrentSave) {
          setIsNewEventAvailable(true);
        }
        dispatch(removeBidAdminEvent(wsEventForCurrentBid.referenceNumber));
      }
    }
    // eslint-disable-next-line
  }, [webSocketEventData.bidAdminEvents]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const loadBidMetaData = () => {
    setLoadErrors(false);
    setIsNewEventAvailable(false);
    currentRequestId.current = null;
    setIsPageLoading(true);
    const referenceNumber = getReferenceNumberFromHref();
    const msaList = getMultiStationAdminStationList(userSession);
    fetchLatestBidEventByReferenceNumber(userSession.eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.event) {
          const bidEvent = getScrubbedBidEvent(response.event);
          if (msaList.includes(bidEvent.station) || userSession.activeStation === bidEvent.station) {
            setBidData(bidEvent);
            setIsPageLoading(false);
            setHasAccessToViewPage(true);
          } else {
            setIsPageLoading(false);
          }
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
        setLoadErrors(true);
      });
  };

  const getReferenceNumberFromHref = () => {
    const uriList = window.location.href.split("/");
    return uriList[uriList.length - 1];
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const renderNewEventSnackbarMessage = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isNewEventAvailable}
        sx={{ width: "100%", marginTop: "50px" }}
      >
        <Alert variant="filled" severity="error">
          <AlertTitle sx={{ textAlign: "left", fontWeight: "bold" }}>Notice</AlertTitle>A new update has been made to
          this bid and your changes are unable to be saved. Please refresh the page to re-enter your changes.
          <Button
            variant="contained"
            color="secondary"
            onClick={() => window.location.reload()}
            sx={{ marginLeft: "15px", color: "secondary.swaBlack" }}
          >
            REFRESH
          </Button>
        </Alert>
      </Snackbar>
    );
  };

  const renderBidContent = () => {
    if (bidData.bidType === BID_TYPE.SHIFT) {
      return (
        <ManageShiftBidPage
          {...props}
          bidData={bidData}
          setBidData={setBidData}
          reloadBidData={loadBidMetaData}
          selectedTab={selectedTab}
          handleChangeTab={handleChangeTab}
          setLoadErrors={setLoadErrors}
          currentRequestId={currentRequestId}
        />
      );
    } else if (bidData.bidType === BID_TYPE.FREEDAY) {
      return (
        <ManageFreedayBidPage
          {...props}
          bidData={bidData}
          setBidData={setBidData}
          reloadBidData={loadBidMetaData}
          selectedTab={selectedTab}
          handleChangeTab={handleChangeTab}
          setLoadErrors={setLoadErrors}
          currentRequestId={currentRequestId}
        />
      );
    } else if (bidData.bidType === BID_TYPE.FTO) {
      return (
        <ManageIAMFTOBidPage
          {...props}
          bidData={bidData}
          setBidData={setBidData}
          reloadBidData={loadBidMetaData}
          selectedTab={selectedTab}
          handleChangeTab={handleChangeTab}
          setLoadErrors={setLoadErrors}
          currentRequestId={currentRequestId}
        />
      );
    } else if (bidData.bidType === BID_TYPE.TWU_DAT_FTO) {
      return (
        <ManageTWUDATFTOBidPage
          {...props}
          bidData={bidData}
          setBidData={setBidData}
          reloadBidData={loadBidMetaData}
          selectedTab={selectedTab}
          handleChangeTab={handleChangeTab}
          setLoadErrors={setLoadErrors}
          currentRequestId={currentRequestId}
        />
      );
    } else if (bidData.bidType === BID_TYPE.IAM_DAT) {
      return (
        <ManageIAMDATBidPage
          {...props}
          bidData={bidData}
          setBidData={setBidData}
          reloadBidData={loadBidMetaData}
          selectedTab={selectedTab}
          handleChangeTab={handleChangeTab}
          setLoadErrors={setLoadErrors}
          currentRequestId={currentRequestId}
        />
      );
    } else if (bidData.bidType === BID_TYPE.NC_PTO_FTO) {
      return (
        <ManageNCPTOFTOBidPage
          {...props}
          bidData={bidData}
          setBidData={setBidData}
          reloadBidData={loadBidMetaData}
          selectedTab={selectedTab}
          handleChangeTab={handleChangeTab}
          setLoadErrors={setLoadErrors}
          currentRequestId={currentRequestId}
        />
      );
    } else if (bidData.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
      return (
        <ManageNCPTOGOCHOLBidPage
          {...props}
          bidData={bidData}
          setBidData={setBidData}
          reloadBidData={loadBidMetaData}
          selectedTab={selectedTab}
          handleChangeTab={handleChangeTab}
          setLoadErrors={setLoadErrors}
          currentRequestId={currentRequestId}
        />
      );
    } else {
      return <></>;
    }
  };

  const renderDesktopManageBidPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    } else if (loadErrors) {
      return (
        <WarningDialog
          isDialogOpen={loadErrors}
          handleClickDialogClose={() => {
            loadBidMetaData();
          }}
          header={"Load Error"}
          message={`Unable to load bid data. The page will refresh when you click OK.`}
          severity={"error"}
        />
      );
    } else if (!hasAccessToViewPage) {
      return <div className="page-error">{NO_ACCESS_TO_PAGE_MESSAGE}</div>;
    }

    return (
      <>
        {renderNewEventSnackbarMessage()}
        {renderBidContent()}
      </>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return isUserAuthorizedStateLoading ? <PageLoading /> : renderDesktopManageBidPage();
};

ManageBidPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default ManageBidPage;
